import { useMemo } from 'react';
import { SpotMarketInfo } from 'src/stores/types';
import {
	MarketType,
	SpotMarketConfig,
	isVariant,
} from '@velocity-exchange/sdk';
import {
	MarketDetails24H,
	MarketId,
	UIMarket,
} from '@velocity-exchange/common';
import useMarketStateStore from 'src/stores/useMarketStateStore';
import useMarketsSortedByCriteria from 'src/hooks/useMarketsSortedByCriteria';
import { getMarketSelectorPricePercentChange } from 'src/components/MarketSelector/utils';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';
import useFavouriteMarkets from 'src/hooks/useFavouriteMarkets';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import { MarketDisplayData } from 'src/components/MarketSelector/MarketSelectorTable/types';
import { useAllPricingData } from 'src/hooks/query/pricing/usePricingData';
import { PricingData } from 'src/hooks/query/pricing/types';
import { pricingDataToMarkPrice } from 'src/hooks/useMarkPrice';

export default function useMarketSelectorSpotMarkets() {
	const spotMarketsLookup = useSpotMarketsLookup();

	const devSwitchIsOn = useDevSwitchIsOn();

	const marketsData = useDriftStore((s) => s.marketsData24H);
	const getMarketHasLiquidity = useMarketStateStore(
		(s) => s.getMarketHasLiquidity
	);
	const getMarketInfoByIndexAndType = useMarketsInfoStore(
		(s) => s.getMarketInfoByIndexAndType
	);
	const { favouriteMarketsIncludes } = useFavouriteMarkets();
	const { data: allPricingData } = useAllPricingData();

	const defaultSortedSpotMarkets = useMarketsSortedByCriteria({
		marketType: MarketType.SPOT,
		sortBy: ['isFavorited', '24hVolume'],
	});

	return useMemo(() => {
		const mappedMarkets = defaultSortedSpotMarkets
			.filter(
				(mkt) =>
					devSwitchIsOn ||
					getMarketHasLiquidity(MarketId.createSpotMarket(mkt.marketIndex))
			)
			.map((mktSortingInfo) => {
				return {
					marketInfo: getMarketInfoByIndexAndType(
						mktSortingInfo.marketIndex,
						mktSortingInfo.marketType
					) as SpotMarketInfo,
					marketConfig: spotMarketsLookup[mktSortingInfo.marketIndex],
				};
			});

		return getSpotMarketsRenderingInfo(
			mappedMarkets,
			marketsData,
			favouriteMarketsIncludes,
			spotMarketsLookup,
			allPricingData
		);
	}, [
		defaultSortedSpotMarkets,
		marketsData,
		favouriteMarketsIncludes,
		spotMarketsLookup,
		devSwitchIsOn,
		getMarketHasLiquidity,
		getMarketInfoByIndexAndType,
		allPricingData,
	]);
}

const getSpotMarketsRenderingInfo = (
	defaultSortedSpotMarkets: {
		marketInfo: SpotMarketInfo;
		marketConfig: SpotMarketConfig;
	}[],
	marketsData: MarketDetails24H[],
	favouriteMarketsIncludes: (marketId: MarketId) => boolean,
	spotMarketsLookup: SpotMarketConfig[],
	allPricingData?: Map<string, PricingData>
): MarketDisplayData[] => {
	return defaultSortedSpotMarkets.map((mktInfo) => {
		const marketConfig = spotMarketsLookup[mktInfo.marketConfig.marketIndex];
		const uiMarket = UIMarket.createSpotMarket(marketConfig.marketIndex);
		const marketId = uiMarket.marketId;

		if (!mktInfo.marketInfo) {
			// Market Info Hasn't Loaded Yet .. Return default/blank values
			return {
				symbol: marketConfig.symbol,
				baseAssetSymbol: marketConfig.symbol,
				market: uiMarket,
				price: null,
				pricePercentChange: null,
				price24hAgo: null,
				volume: null,
				high: null,
				low: null,
				config: marketConfig,
				noInsuranceFund: false,
				isFavorite: favouriteMarketsIncludes(marketId),
			};
		}

		const mkt = mktInfo.marketInfo.config;

		const market24h = marketsData.find(
			(mktData) =>
				mktData.marketIndex === uiMarket.market.marketIndex &&
				isVariant(mktData.marketType, 'spot')
		);
		const markPrice = pricingDataToMarkPrice(allPricingData?.get(marketId.key));

		return {
			symbol: mkt.symbol,
			baseAssetSymbol: uiMarket.uiSymbols.baseAssetSymbol,
			market: uiMarket,
			price: markPrice,
			pricePercentChange: getMarketSelectorPricePercentChange(
				markPrice.toNum(),
				market24h?.price24hAgo ?? 0
			),
			price24hAgo: market24h?.price24hAgo ?? 0,
			volume: market24h?.quoteVolume ?? 0,
			high: market24h?.priceHigh ?? null,
			low: market24h?.priceLow ?? null,
			config: mkt,
			noInsuranceFund: false,
			isFavorite: favouriteMarketsIncludes(marketId),
		};
	});
};
